import React from 'react';
import { BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { CATEGORICAL_PALETTE, STATUS_COLORS } from '../../utils/chartColors';

interface ReportStats {
  total: number;
  pending: number;
  tl_approved: number;
  pm_approved: number;
  deployed: number;
  rejected: number;
  byModule: Record<string, number>;
}

interface ExecutiveSummaryCardProps {
  stats: ReportStats;
  periodLabel: string;
}

const SEQUENTIAL_BLUE = '#3987e5';

const ExecutiveSummaryCard: React.FC<ExecutiveSummaryCardProps> = ({ stats, periodLabel }) => {
  const topModules = Object.entries(stats.byModule)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }))
    .reverse(); // so the largest bar renders at the top

  const statusBreakdown = [
    { name: 'Deployed', value: stats.deployed, color: STATUS_COLORS.good },
    { name: 'Pending', value: stats.pending, color: STATUS_COLORS.warning },
    { name: 'TL Approved', value: stats.tl_approved, color: CATEGORICAL_PALETTE[0] },
    { name: 'PM Approved', value: stats.pm_approved, color: CATEGORICAL_PALETTE[6] },
    { name: 'Rejected', value: stats.rejected, color: STATUS_COLORS.critical },
  ].filter((s) => s.value > 0);

  const kpis = [
    { label: 'Total Deployments', value: stats.total, className: 'bg-blue-600' },
    { label: 'Deployed', value: stats.deployed, className: 'bg-teal-600' },
    { label: 'Pending', value: stats.pending, className: 'bg-violet-600' },
    { label: 'Rejected', value: stats.rejected, className: 'bg-rose-600' },
  ];

  return (
    <div className="rounded-xl shadow-md overflow-hidden border border-gray-200">
      {/* Dark header bar */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-white">
          <BarChart3 className="w-5 h-5" />
          <h3 className="text-lg font-semibold">Deployment Reports Overview</h3>
        </div>
        <span className="text-slate-300 text-sm">{periodLabel}</span>
      </div>

      <div className="p-6 bg-white space-y-6">
        {/* KPI pills */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((kpi) => (
            <div key={kpi.label} className={`rounded-lg p-4 text-white ${kpi.className}`}>
              <p className="text-xs uppercase tracking-wide text-white/80">{kpi.label}</p>
              <p className="text-2xl font-bold mt-1">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Bar + Donut */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Modules by Deployment Count</h4>
            {topModules.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(200, topModules.length * 32)}>
                <BarChart data={topModules} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#898781', fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#52514e', fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill={SEQUENTIAL_BLUE} radius={[0, 4, 4, 0]} name="Deployments" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Status Breakdown</h4>
            {statusBreakdown.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={statusBreakdown}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {statusBreakdown.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name: string) => [`${value} deployments`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummaryCard;
